import * as L from "leaflet";
import { CRS } from "leaflet";

import { ILocation, getLocationBoundingBox } from "./models/ILocation";
import { MAX_ZOOM, TileLayerDefinition } from "./TileLayers";
import { Kernel } from "./Kernel";
import { getModuleLog } from "./Logger";
import { FluentIterable } from "fluent-iterable";
import { PlaceTable } from "./backend/cloud/PlaceTable";

export type MapTileCacheOptions = {
    /** Minimum zoom level to retrieve */
    minZoom: number;

    /** Maximum zoom level to retrieve */
    maxZoom: number;

    /** Cache around place location */
    adaptiveCache: boolean;

    /** Radius around the location to cache */
    radiusMeters: number;

    /** Maximum number of tiles to retrieve before cancelling */
    maxTiles: number;
};

export const DEFAULT_SIZE_LIMIT_MB = 3000;

export function defaultMapTileCacheOptions() {
    return {
        radiusMeters: 100,
        minZoom: 0,
        maxZoom: 19,
        adaptiveCache: true,
        maxTiles: numTileForSizeMb(DEFAULT_SIZE_LIMIT_MB),
    } as MapTileCacheOptions;
}

export type TileIndex = {
    x: number;
    y: number;
    z: number;
};

const APPROX_TILE_SIZE_KB = 25;

export function numTileForSizeMb(size: number) {
    return Math.floor((size * 1024) / APPROX_TILE_SIZE_KB);
}

export function approxSpaceForTilesMb(numTiles: number) {
    return Math.ceil((numTiles * APPROX_TILE_SIZE_KB) / 1024);
}

function makeTileIndexKey(tile: TileIndex) {
    return `${tile.x}/${tile.y}/${tile.z}`;
}

export class MapTileCacher {
    private _kernel = Kernel.instance();

    private _log = getModuleLog("tile-cacher");

    constructor(
        private readonly _layer: TileLayerDefinition,
        private readonly _options: MapTileCacheOptions
    ) {}

    public async updateCacheAroundPlaces(): Promise<string> {
        const filter = this._kernel.currentPlaceFilter.get();
        const allPlaces = this._kernel.dataStore
            .getSource<PlaceTable>("places")
            .allFilteredItems(filter);
        const locations = allPlaces.filter((x) => x.data.locationKnown).map((x) => x.data.location);

        // get detailed tiles around sites
        const tileUrlsToCache = this.adaptiveCacheGetTiles(locations);

        return await this._startDownload(
            tileUrlsToCache,
            `Around locations from zoom level ${this._options.minZoom} to ${this._options.maxZoom} within ${this._options.radiusMeters} meters`
        );
    }

    public async cacheWithinBounds(southWest: ILocation, northEast: ILocation): Promise<string> {
        const tilesToCache = this.cacheWithinBoundsGetTiles(southWest, northEast);
        return await this._startDownload(tilesToCache, `Within bounds cache: ${this._layer.name}`);
    }

    public cacheWithinBoundsGetTiles(southWest: ILocation, northEast: ILocation) {
        const tilesToCache = new Map<string, TileIndex>();
        this._cacheWithinBoundsInternal(southWest, northEast, tilesToCache);
        return [...tilesToCache.values()];
    }

    public async cacheWithinBoundsGetNumTiles(
        southWest: ILocation,
        northEast: ILocation
    ): Promise<{
        numTiles: number;
        maxTilesExceeded: boolean;
    }> {
        return new Promise((resolve) => {
            const tilesToCache = new Map<string, TileIndex>();
            const maxTilesExceeded = !this._cacheWithinBoundsInternal(
                southWest,
                northEast,
                tilesToCache
            );
            resolve({
                numTiles: tilesToCache.size,
                maxTilesExceeded,
            });
        });
    }

    /**
     * Cache down to maximum zoom around the given locations.
     */
    public adaptiveCacheGetTiles(locations: FluentIterable<ILocation>) {
        const tilesToCache = new Map<string, TileIndex>();

        for (const loc of locations) {
            for (let zoom = this._options.minZoom; zoom <= this._options.maxZoom; ++zoom) {
                const bounds = getLocationBoundingBox(loc, this._options.radiusMeters);

                this._cacheWithinBoundsInternal(bounds.min, bounds.max, tilesToCache);
            }
        }

        return [...tilesToCache.values()];
    }

    private async _startDownload(tilesToCache: TileIndex[], msg: string): Promise<string> {
        const tileUrlsToCache = tilesToCache.map((x) => this._getTileUrl(x));

        // get all tiles for the top 3 levels
        this._log.debug(
            `Wants to fetch ${tileUrlsToCache.length} tiles at approx ${approxSpaceForTilesMb(
                tileUrlsToCache.length
            )}mb`
        );

        return (await this._kernel.tileDownloader.startJob(tileUrlsToCache, msg, {})).id;
    }

    private _cacheWithinBoundsInternal(
        southWest: ILocation,
        northEast: ILocation,
        tilesToCache: Map<string, TileIndex>
    ): boolean {
        for (let zoom = this._options.minZoom; zoom <= this._options.maxZoom; ++zoom) {
            // Get to pixel space
            const posMin = this._project(southWest, zoom);
            const posMax = this._project(northEast, zoom);
            const tileBounds = this._pxBoundsToTileRange(L.bounds([posMin, posMax]));

            if (!this._cacheWithinTileBounds(tileBounds, tilesToCache, zoom)) {
                // max tiles reached
                return false;
            }
        }

        return true;
    }

    private _cacheWithinTileBounds(
        tileBounds: L.Bounds | undefined,
        tilesToCache: Map<string, TileIndex>,
        zoom: number
    ): boolean {
        if (tileBounds && tileBounds.min && tileBounds.max) {
            for (let y = tileBounds.min.y; y <= tileBounds.max.y; y++) {
                for (let x = tileBounds.min.x; x <= tileBounds.max.x; x++) {
                    const tile = {
                        x: x,
                        y: y,
                        z: zoom,
                    };
                    const key = makeTileIndexKey(tile);
                    if (!tilesToCache.has(key)) {
                        tilesToCache.set(key, tile);
                        if (tilesToCache.size === this._options.maxTiles) {
                            return false;
                        }
                    }
                }
            }
        }

        return true;
    }
    // -----------------------------------------------------------------
    // These are all copied from Leaflet to remove the dependency on it.
    // -----------------------------------------------------------------

    private _project(location: ILocation, zoom: number) {
        return CRS.EPSG3857.latLngToPoint(
            {
                lat: location.lat,
                lng: location.lon,
            },
            zoom
        );
    }

    private _getSubdomain(tilePoint: TileIndex) {
        if (this._layer.options.subdomains) {
            const index =
                Math.abs(tilePoint.x + tilePoint.y) % this._layer.options.subdomains.length;
            return this._layer.options.subdomains[index];
        }
        return "abc";
    }

    private _pxBoundsToTileRange(bounds: L.Bounds) {
        if (bounds.min && bounds.max) {
            const tileSize = new L.Point(256, 256); // this.getTileSize(); // 256 by default
            return new L.Bounds(
                bounds.min.unscaleBy(tileSize).floor(),
                bounds.max.unscaleBy(tileSize).ceil().subtract([1, 1])
            );
        }
        return undefined;
    }

    /**
     * Modified L.TileLayer.getTileUrl, this will use the zoom given by the parameter coords
     * instead of the maps current zoom level.
     */
    private _getTileUrl(coords: TileIndex) {
        let zoom = coords.z;
        const layer = this._layer;
        const options = layer.options;
        const maxZoom = options.maxZoom ?? MAX_ZOOM;

        if (options.zoomReverse) {
            zoom = maxZoom - zoom;
        }
        zoom += options.zoomOffset ?? 0;
        return L.Util.template(
            layer.url,
            L.extend(
                {
                    r: options.detectRetina && L.Browser.retina && maxZoom > 0 ? "@2x" : "",
                    s: this._getSubdomain(coords),
                    x: coords.x,
                    y: /* options.tms
                        ? (this._layer as any)._globalTileRange.max.y - coords.y 
                        : */ coords.y,
                    z: options.maxNativeZoom ? Math.min(zoom, options.maxNativeZoom) : zoom,
                },
                options
            )
        );
    }
}

// private _startBackgroundFetch(urls: string[]) {
//     navigator.serviceWorker.ready.then(async (swReg: any) => {
//         /* const bgFetch = */ await swReg.backgroundFetch.fetch("my-fetch", urls, {
//             title: "CaveDB - Caching map",
//             icons: [
//                 {
//                     sizes: "300x300",
//                     src: "/ep-5-icon.png",
//                     type: "image/png",
//                 },
//             ],
//             downloadTotal: 1024 * 1024 * 1024,
//         });
//     });
// }
